import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./incomeList.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { TableLoader } from "../../../../../components/LoaderC";
import ListFilters from "./components/ListFilters";
import ListRecords from "./components/ListRecords";
import ListDonutWidget from "./components/ListDonutWidget";
import dayjs from "dayjs";

const INITIAL_LIMIT = 20;
const CHUNK_SIZE = 10;

const isActiveFilter = (value) => value !== "" && value !== "all" && value !== undefined && value !== null;

const IncomeList = ({ handleView }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [sortItem, setSortItem] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [filterredData, setFilterredData] = useState([]);
  const [currentFilterValue, setCurrentFilterValue] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "", property: "", apartment: "", payment_status: "", last_payment_date: null, category: "", rent_month_text: '', rent_month: '' });
  const [propertyList, setPropertyList] = useState([]);
  const [donutGraphData, setDonutGraphData] = useState([]);
  const [apartmentList, setApartmentList] = useState([]);

  const sentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const { property, apartment, category, payment_status: paymentStatus, last_payment_date: lastPaymentDate, rent_month: rentMonth } = filterData;

  useEffect(() => {
    const fetchAllProperties = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/property-list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        let propertyData = response.data?.data || [];
        setPropertyList(propertyData);

        if (localStorage.getItem("propertyFilterData")) {
          let filterPropertyId = localStorage.getItem("propertyFilterData");
          localStorage.removeItem("propertyFilterData");

          setFilterData((prev) => {
            return { ...prev, property: parseInt(filterPropertyId) };
          });

          let apartmentListTemp = [];
          propertyData?.map((i) => {
            if (i.value === parseInt(filterPropertyId)) {
              apartmentListTemp = i.apartments;
            }
            return null;
          });

          setApartmentList(apartmentListTemp || []);
        }

        if (localStorage.getItem("apartmentFilterData")) {
          let apartmentFilter = localStorage.getItem("apartmentFilterData");
          localStorage.removeItem("apartmentFilterData");
          apartmentFilter = parseInt(apartmentFilter);

          setFilterData((prev) => {
            return { ...prev, apartment: parseInt(apartmentFilter) };
          });
        }
      }).catch((error) => {
        console.error("[IncomeList] Failed to load property list for filter", error);
        toast.error(error?.response?.data?.message || "Unable to load properties. Please try again.");
      });
    };
    fetchAllProperties();
  }, [getAccessTokenSilently]);

  const changeSortOrder = (type) => {
    if (type === sortItem) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortOrder("asc");
      }
    } else {
      setSortOrder("asc");
    }
    setSortItem(type);
  };

  const fetchIncomes = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;

    let activeIndicator = "loadingMore";
    if (replace && !hasLoadedOnceRef.current) {
      activeIndicator = "contentLoading";
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = "refetching";
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }
    setLoadMoreError(false);

    let params = { offset: currentOffset, limit: currentOffset === 0 ? INITIAL_LIMIT : CHUNK_SIZE };
    if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();
    if (isActiveFilter(property)) params.property_id = property;
    if (isActiveFilter(apartment)) params.apt_id = apartment;
    if (isActiveFilter(category)) params.category_id = category;
    if (isActiveFilter(paymentStatus)) params.payment_status = paymentStatus;
    if (isActiveFilter(rentMonth)) params.rent_month = rentMonth;
    if (Array.isArray(lastPaymentDate) && lastPaymentDate.length === 2) {
      params.start = dayjs(lastPaymentDate[0]).format("YYYY-MM-DD");
      params.end = dayjs(lastPaymentDate[1]).format("YYYY-MM-DD");
    }
    if (sortItem !== "") {
      params.sort_by = sortItem;
      params.sort_order = sortOrder;
    }

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/incomes-paginated", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      // Ignore responses from requests that were superseded by newer filters/sorting
      if (seq !== requestSeq.current) return;

      const newChunk = response.data?.data || [];
      const meta = response.data?.meta || {};

      setFilterredData((prev) => (replace ? newChunk : prev.concat(newChunk)));
      setOffset(meta?.next_offset ?? currentOffset + newChunk.length);
      setHasMore(Boolean(meta?.has_more));
      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("[IncomeList] Failed to load income list", error);
      toast.error(error?.response?.data?.message || "Unable to load income. Please try again.");

      if (replace) {
        setLoadError(true);
        setFilterredData([]);
        setHasMore(false);
      } else {
        setLoadMoreError(true);
      }
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === "contentLoading") setContentLoading(false);
        else if (activeIndicator === "refetching") setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, debouncedSearch, property, apartment, category, paymentStatus, rentMonth, lastPaymentDate, sortItem, sortOrder]);

  // Debounce search text input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filterData.searchKey || "");
    }, filterData.searchKey ? 400 : 0);

    return () => clearTimeout(handler);
  }, [filterData.searchKey]);

  // Load the first chunk again when search, filters or sorting change
  useEffect(() => {
    fetchIncomes(0, true);
  }, [fetchIncomes]);

  // Infinite scroll: load the next chunk when a sentinel enters the viewport
  useEffect(() => {
    const desktopNode = sentinelRef.current;
    const mobileNode = mobileSentinelRef.current;
    if (!desktopNode && !mobileNode) return;

    const observer = new IntersectionObserver((entries) => {
      const isAnyIntersecting = entries.some((entry) => entry.isIntersecting);
      if (isAnyIntersecting && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
        fetchIncomes(offset, false);
      }
    }, { threshold: 0.1 });

    if (desktopNode) observer.observe(desktopNode);
    if (mobileNode) observer.observe(mobileNode);

    return () => observer.disconnect();
  }, [hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchIncomes, filterredData.length]);

  useEffect(() => {
    getDonutGraphData("");
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const buildUrl = () => {
      if (currentFilterValue === "search" || currentFilterValue === "payment_status")
        return;
      const queryParams = [];

      if (filterData.property && filterData.property !== "all") {
        queryParams.push(`property_id=${filterData.property}`);
      }

      if (filterData.apartment && filterData.apartment !== "all") {
        queryParams.push(`apt_id=${filterData.apartment}`);
      }

      if (Array.isArray(filterData.last_payment_date) && filterData.last_payment_date.length === 2) {
        const start = filterData.last_payment_date[0]?.toLocaleDateString("en-CA");
        const end = filterData.last_payment_date[1]?.toLocaleDateString("en-CA");

        queryParams.push(`start=${start}`);
        queryParams.push(`end=${end}`);
      }

      if (filterData.category && filterData.category !== "all") {
        queryParams.push(`category_id=${filterData.category}`);
      }

      if (filterData.rent_month && filterData.rent_month !== "all") {
        queryParams.push(`rent_month=${filterData.rent_month}`);
      }

      let queryParamsUrl = "";

      if (queryParams.length) {
        queryParamsUrl = `?${queryParams.join("&")}`;
      }

      getDonutGraphData(queryParamsUrl);
    };

    if (currentFilterValue) {
      buildUrl();
    }
  }, [currentFilterValue, filterData]);

  useEffect(() => {
    const getCategoryList = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/income-categories", {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then((response) => {
        setIncomeCategories(response.data?.data || []);

        if (localStorage.getItem("incomeCategoryFilterData")) {
          let categoryFilter = localStorage.getItem("incomeCategoryFilterData");
          localStorage.removeItem("incomeCategoryFilterData");
          categoryFilter = parseInt(categoryFilter);

          setFilterData((prev) => {
            return { ...prev, category: parseInt(categoryFilter) };
          });

          setCurrentFilterValue("category");
        }
      }).catch((error) => {
        console.error("[IncomeList] Failed to load category list for filter", error);
        toast.error(error?.response?.data?.message || "Unable to load income categories. Please try again.");
      });
    };

    getCategoryList();
  }, [getAccessTokenSilently]);

  const selectProperty = (e) => {
    let apartmentListTemp = [];
    propertyList?.map((i) => {
      if (i.value === e) {
        apartmentListTemp = i.apartments;
      }
      return null;
    });

    setApartmentList(apartmentListTemp || []);
  };

  async function getDonutGraphData(queryParams) {
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
    let url = process.env.REACT_APP_PAYMENT_API_URL + "v1/income-data";

    if (queryParams) {
      url = url + queryParams;
    }
    axios(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then((response) => {
      setDonutGraphData(response.data.data);
    }).catch((error) => { });
  }

  return (<div className={`${styles.BodyCon}`}>
    <div className={`${styles.mainCardDiv}`}>
      <div className={`${styles.mainCard}`}>
        <div className="control-pane">
          {contentLoading && (<div className="Loader TableLoader">
            <TableLoader />
          </div>)}

          <div className={`${styles.Row}`}>
            {!contentLoading && (<>
              <ListFilters
                setFilterData={setFilterData}
                setFilterOpen={setFilterOpen}
                filterOpen={filterOpen}
                filterData={filterData}
                selectProperty={selectProperty}
                propertyList={propertyList}
                apartmentList={apartmentList}
                handleView={handleView}
                setCurrentFilterValue={setCurrentFilterValue}
                incomeCategories={incomeCategories}
              />

              <div className={`${styles.IncomeListingBody}`}>
                <div className={refetching ? `${styles.IncomeListSec} ${styles.Refetching}` : `${styles.IncomeListSec}`}>
                  {filterredData.length > 0 && (<ListRecords
                    filterredData={filterredData}
                    filterData={filterData}
                    sortItem={sortItem}
                    sortOrder={sortOrder}
                    changeSortOrder={changeSortOrder}
                    sentinelRef={sentinelRef}
                    mobileSentinelRef={mobileSentinelRef}
                  />)}

                  {loadingMore && (<div className={`${styles.ScrollStatus}`}>
                    <div className={`${styles.scrollLoaderWrap}`}>
                      <div className={`${styles.scrollSpinner}`} />
                      <span>Loading more income...</span>
                    </div>
                  </div>)}

                  {loadMoreError && (<div className={`${styles.ScrollStatus}`}>
                    <p>Couldn't load more income.</p>
                    <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchIncomes(offset, false)}>Retry</button>
                  </div>)}

                  {filterredData.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                    <p className={`${styles.NoDataText}`}>Something went wrong!</p>
                    <p className={`${styles.NoDataTextSub}`}>We couldn't load the income list.</p>
                    <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchIncomes(0, true)}>Retry</button>
                  </div>)}

                  {filterredData.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                    <div className={`${styles.NoDataIMG}`}>
                      <img src="/static/img/no-income.png" alt="No Data" />
                    </div>
                    <p className={`${styles.NoDataText}`}>Income list is currently empty.</p>
                    <p className={`${styles.NoDataTextSub}`}>All your income entries will appear here once available.</p>
                  </div>)}
                </div>

                <div className={`${styles.IncomeGraphSec}`}>
                  <ListDonutWidget donutGraphData={donutGraphData} />
                </div>
              </div>

            </>)}
          </div>
        </div>
      </div>
    </div>
  </div>);
};

export default IncomeList;